import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";
import { validatePassword } from "../lib/passwordPolicy";
import PasswordRules from "../components/PasswordRules";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();

  if (!token) {
    return (
      <div className="container mx-auto px-4 py-20 max-w-md text-center">
        <XCircle className="mx-auto text-destructive" size={56} />
        <h1 className="mt-6 font-display text-3xl font-black text-ink">Lien invalide</h1>
        <p className="mt-2 text-subtle">Le lien de réinitialisation est introuvable ou malformé.</p>
        <Button asChild className="mt-6 bg-brand hover:bg-brand-hover text-white rounded-md h-12 px-8" data-testid="back-forgot-btn">
          <Link to="/forgot-password">Demander un nouveau lien</Link>
        </Button>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const v = validatePassword(pwd);
    if (!v.ok) {
      setError(v.message);
      return;
    }
    if (pwd !== pwd2) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, newPassword: pwd });
      setDone(true);
      toast.success("Mot de passe mis à jour");
      setTimeout(() => nav("/login"), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur");
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="container mx-auto px-4 py-20 max-w-md text-center">
        <CheckCircle2 className="mx-auto text-[#34C759]" size={56} />
        <h1 className="mt-6 font-display text-3xl font-black text-ink">Mot de passe mis à jour</h1>
        <p className="mt-2 text-subtle">Redirection vers la page de connexion…</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 md:px-8 py-16 max-w-md">
      <p className="overline mb-2">Réinitialisation</p>
      <h1 className="font-display text-3xl font-black tracking-tight text-ink">Nouveau mot de passe</h1>
      <p className="mt-2 text-subtle">Choisissez un nouveau mot de passe pour votre compte.</p>

      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
        <div>
          <Label>Nouveau mot de passe</Label>
          <Input type="password" required value={pwd} onChange={(e) => setPwd(e.target.value)} className="h-12 mt-1.5" data-testid="pwd-input" />
          <PasswordRules password={pwd} />
        </div>
        <div>
          <Label>Confirmer</Label>
          <Input type="password" required value={pwd2} onChange={(e) => setPwd2(e.target.value)} className="h-12 mt-1.5" data-testid="pwd2-input" />
        </div>
        {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full h-12 bg-brand hover:bg-brand-hover rounded-md text-white font-medium" data-testid="submit-btn">
          {loading ? "…" : "Mettre à jour le mot de passe"}
        </Button>
      </form>
    </div>
  );
}
